/**
 * Limita `n` al intervalo cerrado `[lo, hi]`.
 */
export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Aproxima `value` al múltiplo de `step` más cercano desde `min`, y acota a `[min, max]`.
 */
export function snapToStep(value: number, min: number, max: number, step: number): number {
  if (!(step > 0) || !Number.isFinite(step)) {
    return clamp(value, min, max);
  }
  const snapped = min + Math.round((value - min) / step) * step;
  // Evita errores de coma flotante (p. ej. 0.30000000004)
  const rounded = Number(snapped.toPrecision(12));
  return clamp(rounded, min, max);
}

/**
 * Garantiza `start <= end` y ambos dentro de `[min, max]`, aplicando `step` a cada extremo.
 * Si el valor de entrada cruza extremos, **ordena** (útil para estado controlado inválido);
 * durante la interacción usa {@link clampStartAgainstEnd} / {@link clampEndAgainstStart}.
 */
export function normalizeRangeValue(
  raw: { start: number; end: number },
  min: number,
  max: number,
  step: number
): { start: number; end: number } {
  let start = snapToStep(raw.start, min, max, step);
  let end = snapToStep(raw.end, min, max, step);
  if (start > end) {
    const t = start;
    start = end;
    end = t;
  }
  return { start, end };
}

/**
 * Mueve solo el extremo **inicio** hacia `newStartRaw`, manteniendo `end` fijo.
 * No intercambia extremos: si chocan, el inicio no supera a `end` (ambos pueden quedar iguales).
 */
export function clampStartAgainstEnd(
  newStartRaw: number,
  end: number,
  min: number,
  max: number,
  step: number
): { start: number; end: number } {
  const endSnap = snapToStep(end, min, max, step);
  let start = snapToStep(newStartRaw, min, max, step);
  start = clamp(start, min, endSnap);
  return { start, end: endSnap };
}

/**
 * Mueve solo el extremo **fin** hacia `newEndRaw`, manteniendo `start` fijo.
 * No intercambia extremos: si chocan, el fin no baja de `start`.
 */
export function clampEndAgainstStart(
  start: number,
  newEndRaw: number,
  min: number,
  max: number,
  step: number
): { start: number; end: number } {
  const startSnap = snapToStep(start, min, max, step);
  let end = snapToStep(newEndRaw, min, max, step);
  end = clamp(end, startSnap, max);
  return { start: startSnap, end };
}
